import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { map } from 'lodash';

import {
  IMembershipPlan,
  IMembershipPlanPrice,
  IWidget,
  EMembershipSwagBagItem,
} from '../../types/model';
import { WelcomePack, getPlanClasses, CheckWithLabel } from './Utils';

interface PropTypes {
  membershipPlan: IMembershipPlan;
  renewal: 'MONTHLY' | 'YEARLY';
  widgets: any;
  swagBag: EMembershipSwagBagItem[];
}

const PlanFeatures: React.FC<PropTypes> = ({
  membershipPlan,
  renewal,
  widgets,
  swagBag,
}) => {
  const { t } = useTranslation();

  // console.log(membershipPlan.features);

  const planClasses = getPlanClasses(membershipPlan);

  const price = membershipPlan.prices.find(
    ({ renewal: priceRenewal }) => priceRenewal === renewal
  ) as IMembershipPlanPrice;
  const renewalShortName =
    renewal === 'MONTHLY'
      ? t('choosePlan.monthShort')
      : t('choosePlan.yearShort');

  const features = Object.keys(membershipPlan.features || {}).filter(
    (f) => f !== '__typename'
  );

  const widgetsByCategories = useMemo(() => {
    if (!widgets || !widgets.items) {
      return [];
    }

    const byCategories: { [id: string]: [IWidget] } = {};

    widgets.items.forEach((widget: IWidget) => {
      byCategories[widget.category] = byCategories[widget.category] || [];

      byCategories[widget.category].push(widget);
    });

    return byCategories;
  }, [widgets]);

  return (
    <div className="w-full shadow-lg mb-4 rounded-lg bg-white">
      <div
        className={`px-4 py-2 ${planClasses} text-lg font-semibold rounded-tl-lg rounded-tr-lg`}
      >
        {membershipPlan.title}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 text-left flex flex-col flex-wrap w-full gap-4">
          <div className="text-lg font-semibold">
            {price.price === 0 ? (
              t('choosePlan.free')
            ) : (
              <>
                <span className="font-semibold">${price.price}</span>/
                {renewalShortName}
              </>
            )}
          </div>
          <div>{t(`packages.${membershipPlan.title}`)}</div>
          {renewal !== 'MONTHLY' && membershipPlan.sku !== 'Camelot' && (
            <div className="flex">
              <WelcomePack className="px-3 py-2 font-semibold" />
            </div>
          )}
          <div className="mt-2 flex flex-col gap-2">
            {swagBag.map((swag, index) => (
              <React.Fragment key={`${swag}-${index}`}>
                <CheckWithLabel
                  active={
                    renewal === 'YEARLY' &&
                    membershipPlan.features.swagBag.indexOf(swag) !== -1
                  }
                  label={t('checkout.features.swagBag.' + swag)}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
        <div>
          <div className="mt-6 mb-6 flex flex-col gap-2">
            {features.map((feature, index) => {
              const featureValue = membershipPlan.features[feature];

              let el: React.ReactNode = null;

              if (feature === 'strategies') {
                el = map(
                  widgetsByCategories,
                  (categorizedWidgets: any, category) => {
                    const amount = categorizedWidgets.filter(
                      ({ membershipPlans }) =>
                        !!membershipPlans.items.find(
                          ({ membershipPlan: plan }) =>
                            plan.id === membershipPlan.id
                        )
                    ).length;

                    // console.log({ categorizedWidgets });

                    return (
                      <CheckWithLabel
                        key={category}
                        label={t('choosePlan.' + category + '.0', { amount })}
                        active={true}
                      />
                    );
                  }
                );
              }

              return (
                <React.Fragment key={`${feature}-${index}`}>
                  {el}
                  <CheckWithLabel
                    active={
                      feature === 'swagBag'
                        ? membershipPlan.title !== 'Camelot'
                        : featureValue
                    }
                    label={
                      typeof featureValue === 'number'
                        ? featureValue
                          ? featureValue < 0
                            ? t('choosePlan.' + feature + 'Unlimited')
                            : t('choosePlan.' + feature, {
                                amount: featureValue,
                              })
                          : t('choosePlan.' + feature + 'Empty')
                        : t('checkout.features.label.' + feature)
                    }
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanFeatures;
